import PdfDocumentWrapper from '../../PdfDocumentWrapper'
import { useFetchFromServer } from '../../api/fetchHook'
import { useCallback, useMemo } from 'react'
import { useQuery, useQueryClient } from 'react-query'
import { mergeFilesKeyFactory } from './mergeFilesKeyFactory'
import { DocumentMetadata } from '../../../../shared/src/data/documentMetadata'

export const useMergeFiles = (
  projectId?: string,
  scanId?: string,
  documents?: PdfDocumentWrapper[],
) => {
  const { fetchJsonFromServer } = useFetchFromServer()
  const queryClient = useQueryClient()

  const transformPdfDocumentWrappers = (
    documents: PdfDocumentWrapper[],
  ): DocumentMetadata[] => {
    return documents.map<DocumentMetadata>((doc) => ({
      fileId: doc.id,
      fileName: doc.name,
      totalNumOfPages: doc.document.numPages,
    }))
  }

  const mergeFiles = useQuery<string, unknown, string>(
    mergeFilesKeyFactory.merge(projectId, scanId, documents),
    useCallback(async () => {
      if (!projectId || !scanId || !documents) {
        throw new Error('Invalid parameters')
      }

      const transformedDocumentsMetadata =
        transformPdfDocumentWrappers(documents)
      let { signedUrl } = await fetchJsonFromServer<{ signedUrl: string }>(
        process.env.PUBLIC_URL + '/api/mergeFiles',
        {
          method: 'POST',
          body: JSON.stringify({
            projectId,
            scanId,
            documentsMetadata: transformedDocumentsMetadata,
          }),
          headers: { 'Content-Type': 'application/json' },
        },
      )
      return signedUrl
    }, [fetchJsonFromServer, projectId, scanId, documents]),
    {
      retry: false,
      enabled: false,
      onSuccess: async (signedUrl) => {
        await queryClient.invalidateQueries(
          mergeFilesKeyFactory.merge(projectId, scanId, documents),
        )
        window.location.href = signedUrl
      },
    },
  )

  return useMemo(() => ({ mergeFiles }), [mergeFiles])
}
